// To use this module make sure to run, yarn add -D @types/facebook-js-sdk
import { trace, isDebug } from "../debug";

let embedInjected = false;
export let embedLoaded = false;
// eslint-disable-next-line
const callbacks: any = [];

/**
 * Checks the user's login status and initializes the Facebook API.
 *
 * @param {string} facebookId The appId for the Facebook app.
 * @param {Object} options The options object to use in FB.init (optional). \
 * (see https://developers.facebook.com/docs/javascript/reference/FB.init/v2.11)
 * @returns {Promise} Returns a promise with the FBLoginStatus response.
 */
// eslint-disable-next-line
export function checkLoginStatus(facebookId: any, options: any): Promise<any> {
  if (typeof options !== "object") {
    options = {
      autoLogAppEvents: true,
      status: true,
      xfbml: true,
      cookie: true,
      version: "v4.0",
    };
  }

  options.appId = facebookId;

  // TODO : Take this out later.
  // if (location.hostname === "localhost") {
  // 	return Promise.resolve();
  // }

  // Check for blocking of facebook

  return new Promise((resolve, reject) => {
    // Setup callback in window.
    // eslint-disable-next-line
    (window as any).fbAsyncInit = () => {
      FB.init(options);
      embedLoaded = true;
      // Get login status.

      FB.getLoginStatus((response: fb.StatusResponse) => {
        if (response.status === "connected" && response.authResponse) {
          trace("Resolved (logged in FB)");
          trace(response);
          resolve(response);
        } else {
          trace("Rejected... (not logged in FB)");
          trace(response);
          reject(response);
        }
      });
    };
    embedInjected = true;
    // Insert the Facebook SDK into the document.
    ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      const js = d.createElement(s);
      js.id = id;
      // eslint-disable-next-line
      (js as any).src = `https://connect.facebook.net/en_US/sdk${
        isDebug ? "/debug" : ""
      }.js`;
      // eslint-disable-next-line
      (fjs as any).parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  });
}

/**
 * Embeds the facebooksdk and then calls a callback when done.
 * If the SDK is in the middle of loading, add the callback into an array for later execution.
 */
// eslint-disable-next-line
export function embed(facebookId: string, options?: fb.InitParams, cb?: any) {
  // if (typeof options !== "object") {
  options = options || { appId: facebookId };
  options.version = options.version || "v4.0";
  // if (!options.version) {
  // 	options = {
  // 		autoLogAppEvents: true,
  // 		status: true,
  // 		xfbml: true,
  // 		cookie: true,
  // 		version: "v2.11"
  // 	};
  // }

  options.appId = facebookId;

  if (!embedInjected) {
    embedInjected = true;
    if (cb) {
      callbacks.push(cb);
    }
    // eslint-disable-next-line
    (window as any).fbAsyncInit = () => {
      embedLoaded = true;
      // eslint-disable-next-line
      FB.init(options!);
      for (const callback of callbacks) {
        callback();
      }
    };

    // Insert the Facebook SDK into the document.
    ((d, s, id) => {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      const js = d.createElement(s);
      js.id = id;
      // eslint-disable-next-line
      (js as any).src = `https://connect.facebook.net/en_US/sdk${
        isDebug ? "/debug" : ""
      }.js`;
      // eslint-disable-next-line
      (fjs as any).parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  } else if (embedLoaded) {
    if (cb) {
      cb();
    }
  } else {
    if (cb) {
      callbacks.push(cb);
    }
  }
}

export function embedPromise(
  // eslint-disable-next-line
  facebookId: any,
  // eslint-disable-next-line
  options?: any
): Promise<fb.FacebookStatic> {
  return new Promise((resolve) => {
    embed(facebookId, options, () => resolve(FB));
  });
}

export function checkAdBlock() {
  // eslint-disable-next-line
  if (!(checkAdBlock as any).promise) {
    // eslint-disable-next-line
    (checkAdBlock as any).promise = new Promise((resolve, reject) => {
      const time = Date.now();
      const img = new Image();
      img.onload = resolve;
      img.onerror = () => {
        if (Date.now() - time < 50) {
          reject();
        } else {
          // the request took to long, it seams this is a real network error
          resolve();
        }
      };
      img.src = "//apps.facebook.com/favicon.ico";
    });
  }
  // eslint-disable-next-line
  return (checkAdBlock as any).promise;
}

/**
 * Requests user info with email, name, uid, friends list, and granted permissions.
 */
// eslint-disable-next-line
export function requestInfo(uid: any) {
  return new Promise((resolve) => {
    // Handler for batch getting permissions and user fields.
    // eslint-disable-next-line
    const handler = (response: any) => {
      const user = JSON.parse(response[0].body);
      const name = user.name;
      const email = user.email;
      const userid = user.id;
      // const friends = JSON.parse(response[1].body).data.map(
      // 	(friend) => friend.id
      // );
      const perms = JSON.parse(response[1].body).data;

      trace(perms);

      resolve({
        name,
        userid,
        email,
        perms,
      });
    };

    const batch = [
      {
        method: "GET",
        // eslint-disable-next-line
        relative_url: "me?fields=id,name,email",
      },
      {
        method: "GET",
        // eslint-disable-next-line
        relative_url: `${uid}/permissions`,
      },
      // { method: "GET", relative_url: `${uid}/friends` },
    ];

    // eslint-disable-next-line
    (window as any).FB.api(
      "/",
      "POST",
      {
        batch,
      },
      handler
    );
  });
}

/**
 * Pulls up a facebook login prompt. Make sure to trigger through mouse click as to not
 * trigger popup blockers.
 *
 * @returns {Promise} Returns a promise with the FBLoginStatus response.
 */
// eslint-disable-next-line
export function login(): Promise<any> {
  return new Promise((resolve, reject) => {
    FB.login(
      // eslint-disable-next-line
      (response: any) => {
        trace("Got FB login response.");
        if (response.status === "connected" && response.authResponse) {
          resolve(response);
        } else {
          reject(response);
        }
      },
      {
        scope: "public_profile,email",
        // eslint-disable-next-line
        return_scopes: true,
        // eslint-disable-next-line
        auth_type: "rerequest",
      }
    );
  });
}

/**
 * Logs the user out of facebook.
 *
 * @returns {Promise} Returns a promise for when the logout is complete.
 */
export function logout() {
  return new Promise((resolve) => {
    // eslint-disable-next-line
    FB.logout((response: any) => {
      trace(response);
      resolve();
    });
  });
}

/**
 * Triggers a facebook share
 * @param url The full url to share (e.g. https://someshare.com/article )
 * @param text The text to share
 */
export function share(url: string, text: string) {
  FB.ui(
    {
      method: "share",
      href: url,
      quote: text,
    },
    (response) => {
      // eslint-disable-next-line
      if (response && response.hasOwnProperty("error_code")) {
        switch (response.error_code) {
          case 4201:
            break;
        }
      }
    }
  );
}
